import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Directive, ElementRef, HostListener, Input, OnInit } from '@angular/core';

@Directive({
  standalone: true,
  selector: 'input[vsIntegerInput]',
})
export class IntegerInputDirective implements OnInit {
  private _allowNegative = false;

  @Input()
  get vsAllowNegative(): boolean {
    return this._allowNegative;
  }
  set vsAllowNegative(value: boolean | string) {
    this._allowNegative = coerceBooleanProperty(value);
  }

  @Input() vsMin?: number;
  @Input() vsMax?: number;

  constructor(private readonly el: ElementRef<HTMLInputElement>) {}

  ngOnInit(): void {
    const input = this.el.nativeElement;
    input.setAttribute('step', '1');

    const effectiveMin = this.vsMin ?? (this._allowNegative ? undefined : 0);
    if (effectiveMin !== undefined) input.setAttribute('min', String(effectiveMin));
    if (this.vsMax !== undefined) input.setAttribute('max', String(this.vsMax));
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length > 1) return;

    const isDigit = /^[0-9]$/.test(event.key);
    const isAllowedMinus = event.key === '-' && this._allowNegative;
    if (!isDigit && !isAllowedMinus) {
      event.preventDefault();
    }
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    // Only block value changes while the input is focused — page scroll over an
    // unfocused input still works as normal.
    if (document.activeElement === this.el.nativeElement) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    const sanitized = this.sanitize(pasted);

    event.preventDefault();
    const input = this.el.nativeElement;

    // <input type="number"> doesn't support selection APIs in most browsers:
    // selectionStart/End return null, and setSelectionRange throws InvalidStateError.
    // Fall back to appending sanitized text to the existing value for those.
    const supportsSelection = input.type !== 'number';
    const start = supportsSelection ? (input.selectionStart ?? input.value.length) : input.value.length;
    const end = supportsSelection ? (input.selectionEnd ?? input.value.length) : input.value.length;
    input.value = input.value.slice(0, start) + sanitized + input.value.slice(end);

    if (supportsSelection) {
      const newPos = start + sanitized.length;
      input.setSelectionRange(newPos, newPos);
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private sanitize(value: string): string {
    if (!this._allowNegative) {
      return value.replace(/[^0-9]/g, '');
    }
    const negative = value.trim().startsWith('-');
    const digits = value.replace(/[^0-9]/g, '');
    return negative ? `-${digits}` : digits;
  }
}
